import React, { useEffect, useState } from "react";
import {
  Copy,
  Check,
  Users,
  Award,
  Mail,
  PhoneIcon,
  Wallet,
} from "lucide-react";
import PrimaryCard from "../../../components/cards/PrimaryCard";
import StatCard from "./sections/StatCard";
import InfoRow from "./sections/InfoRow";
import ActivityItem from "./sections/ActivityItem";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { fetchMarketerDashboard } from "../../../redux/features/adminSlice";

const AffiliateDashboard: React.FC = () => {
  const { adminProfile, marketerDashboard } = useAppSelector(
    (state) => state.admin
  );
  const [copied, setCopied] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const handleCopy = (): void => {
    navigator.clipboard.writeText(marketerDashboard?.referrerCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    dispatch(fetchMarketerDashboard());
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="w-full mx-auto p-4 sm:p-6 lg:p-8">
        <h1 className="text-lg sm:text-xl font-semibold text-gray-500">
          Dashboard Overview
        </h1>
      </div>
      <main className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Banner Alert */}
        <div className="flex flex-col sm:flex-row w-full gap-8 mb-6 ">
          <div className="bg-yellow-400 rounded-lg p-4 flex-1 flex items-start max-h-[110px] overflow-hidden">
            <div className="flex-1 flex gap-4">
              <p className="font-bold w-[60%]  text-gray-900 text-sm sm:text-[20px]">
                Rise from entry level to become a marketing manager on Intasida
              </p>
              <img
                src="/assets/images/png/star-bg.png"
                alt=""
                className="h-28 w-50"
              />
            </div>
          </div>
          <PrimaryCard className="flex-1 flex flex-col justify-between">
            <p className="font-semibold text-lg text-gray-900 mb-2">
              My Referral Code
            </p>
            <div className="flex gap-2 items-center">
              <span>{marketerDashboard?.referrerCode}</span>
              <span onClick={handleCopy}>
                {copied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </span>
            </div>
          </PrimaryCard>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4  lg:col-span-3 ">
            <StatCard
              icon={Wallet}
              value={"₦ " + (marketerDashboard?.netProfit || 0)}
              label="Net Profit"
              iconBg="bg-blue-50"
            />
            <StatCard
              icon={Wallet}
              value={"₦ " + (marketerDashboard?.referralEarned || 0)}
              label="Referral Earned"
              iconBg="bg-green-50"
            />
            <StatCard
              icon={Users}
              value={marketerDashboard?.numberOfReferrals || 0}
              label="Number of Referrals"
              iconBg="bg-purple-50"
            />
            <StatCard
              icon={Award}
              value={`Level ${marketerDashboard?.levelProgress?.currentLevel}`}
              label="Progress level"
              iconBg="bg-orange-50"
            />
          </div>
          {/* Middle Column - Profile */}
          <div className="lg:col-span-9">
            <PrimaryCard className="flex flex-col sm:flex-row gap-4 h-full">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">About</h2>
                  {/* <button className="px-4 py-2 text-sm border-2 border-gray-300 rounded-full text-primary hover:bg-gray-50 transition-colors">
                    Edit Info
                  </button> */}
                </div>

                <div className="flex flex-col sm:flex-row items-center text-center mb-6 gap-3">
                  <div className="w-20 h-20 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full mb-3 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">OA</span>
                  </div>
                  <div className="flex flex-col text-start">
                    <h4 className="font-semibold text-gray-900">
                      {adminProfile?.firstName + " " + adminProfile?.lastName}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {adminProfile?.roles}
                    </p>
                  </div>
                </div>

                <div className="space-y-1 divide-y divide-gray-100">
                  <InfoRow
                    icon={Mail}
                    label="Email"
                    value={adminProfile?.email || ""}
                  />
                  <InfoRow
                    icon={PhoneIcon}
                    label="Phone"
                    value={adminProfile?.phoneNumber || ""}
                  />
                  {/* <InfoRow icon={Gende} label="Gender" value="Male" /> */}
                  {/* <InfoRow
                  icon={LocationIcon}
                  label="Address"
                  value="Off Adegaye street Abuja Nigeria"
                /> */}
                </div>

                {/* <div className="mt-6 p-3 bg-purple-50 border border-blue-400 rounded-lg">
                  <p className=" flex items-center gap-2 text-sm text-primary text-center">
                    <BadgeInfo />{" "}
                    <span>
                      Complete your information to start referring candidates.{" "}
                    </span>
                    <Link
                      to="#"
                      className="font-semibold underline text-primary"
                    >
                      Go to settings!
                    </Link>
                  </p>
                </div> */}
              </div>

              {/* <div className="flex items-center justify-between">
                <CircularProgress percentage={50} />
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    Profile Completion
                  </p>
                  <p className="text-xs text-gray-500">
                    Complete your profile to unlock all features
                  </p>
                </div>
              </div> */}
            </PrimaryCard>
          </div>
          {/* Right Column - Activity & Link */}
          <div className="lg:col-span-4 space-y-4">
            {/* Recent Activity */}
          </div>{" "}
        </div>{" "}
        <PrimaryCard>
          <h3 className="font-semibold text-gray-900 mb-4">Recent earnings</h3>

          <div className="space-y-0">
            {marketerDashboard?.earningsTimeline?.length > 0 ? (
              marketerDashboard.earningsTimeline.map(
                (item: any, index: any) => {
                  const formattedDate = new Date(item.date).toLocaleDateString(
                    "en-US",
                    { year: "numeric", month: "short", day: "numeric" }
                  );

                  return (
                    <ActivityItem
                      key={index}
                      date={formattedDate}
                      message={`You earned ₦${item.earnings.toLocaleString()} from ${
                        item.purchaseCount
                      } purchase(s).`}
                    />
                  );
                }
              )
            ) : (
              <p className="text-center text-gray-700 font-semibold text-lg italic py-6">
                No recent earnings available.
              </p>
            )}
          </div>
        </PrimaryCard>
      </main>
    </div>
  );
};

export default AffiliateDashboard;
